/*
 * Shared canvas-based signature pad. Extracted from DriverTripApp's
 * SignatureScreen so contract e-sign, incident reports, and rider POD
 * all use the same DPR-aware, touch-friendly pen.
 *
 * Usage:
 *   <SignaturePad
 *     onChange={(dataUrl) => setSig(dataUrl)}
 *     height={220}
 *     ariaLabel="Sign contract here"
 *   />
 *
 * The parent decides how / when to submit — this component just draws
 * and exposes the PNG dataURL. `emptyRatioThreshold` guards a "sign here"
 * validation without doing anything else fancy.
 */
import React, { useEffect, useRef, useState } from "react";

export function SignaturePad({ onChange, height = 200, ariaLabel = "Signature pad" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = c.getBoundingClientRect();
    c.width = rect.width * dpr;
    c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = "#0F172A";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const ptFromEvent = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches?.[0] || e.changedTouches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => { e.preventDefault(); drawing.current = true; lastPt.current = ptFromEvent(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = ptFromEvent(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPt.current = p;
    if (!hasInk) setHasInk(true);
    onChange?.(canvasRef.current.toDataURL("image/png"));
  };
  const end = () => { drawing.current = false; lastPt.current = null; };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <div style={{ background: "#fff", border: "1px solid #E2E0D8", borderRadius: 12, padding: 8 }}>
      <canvas
        ref={canvasRef}
        aria-label={ariaLabel}
        data-testid="signature-pad-canvas"
        style={{ display: "block", width: "100%", height, background: "#fff", touchAction: "none", borderRadius: 8 }}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, alignItems: "center" }}>
        <div style={{ fontSize: 11, color: "#64748B" }}>Sign above — X ______________</div>
        <button type="button" onClick={clear} data-testid="signature-pad-clear" style={{ background: "none", border: "none", color: "#2B4C3B", fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
          Clear
        </button>
      </div>
    </div>
  );
}
